/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {useEffect, useMemo, useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {format} from 'date-fns'
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import {useTranslation} from 'react-i18next'
import ProductsOrderStyle from 'pages/ProductOrderPages/style'
import useProductManagement from 'features/productManagement/useProductManagement'
import useOrganization from 'features/org/useOrganization'
import {
  createChargeProductApi,
  createOrderProductApi,
  fetchProductDetailUserApi,
  getLicenseExpiredDate,
  OfferItem,
  paymentInformationApi,
  ProductUserDetailResponse,
  ShopOrderResponse,
} from 'api/productApi'
import {
  DEFAULT_ONE_QUANTITY,
  PAYMENT_METHOD,
  PBM_INDICATOR_ID,
  PREPAYMENT_ITEM,
  TYPE_INDICATOR_PRODUCT,
  UNIT_PRICES,
  USAGE_PERIOD_UNIT,
} from 'pages/ProductsPages/constant'
// eslint-disable-next-line import/no-extraneous-dependencies
import PortOne from '@portone/browser-sdk'
import StompClient from 'pages/ProductOrderPages/WebSocket/StompClient'
import {getQueryFromUrl} from 'helpers/commonHelper'
import {navigate} from 'AppHistory'
import RouteConstant from 'constants/RouteConstant'
import {useForm} from 'react-hook-form'
import useFailureModal from 'features/modal/useFailureModal'
import {
  NATION_CODE,
  PACKAGE_AVAILABLE_IMAGE_ID,
  PAYMENT_FAILED,
} from 'pages/ProductOrderPages/constants'
import {dateToDashString} from 'helpers/dateHelper'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'
import {useAppDispatch} from 'hooks'
import {
  openReportImageDialog,
  setReportDialogId,
} from 'features/modal/modalSlice'

const PURCHASE = 'purchase'
const CHARGE = 'charge'

const useStyles = makeStyles(ProductsOrderStyle)
const ProductOrderPayment = () => {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const {onOpen: onOpenFail} = useFailureModal()
  const {id: productId} = useParams<{id: string}>()
  const {
    getPackageName,
    getPackageList,
    getCLS,
    isAgreeToPurchase,
    onSetIsAgreeToPurchase,
    getOrder,
  } = useProductManagement()
  const [data, setData] = useState<ProductUserDetailResponse | null>(null)
  const location = useLocation()
  const searchParams = getQueryFromUrl(location.search)
  const {organization} = useOrganization()
  const [paymentId, setPaymentId] = useState<string>('')
  const [packageEndDate, setPackageEndDate] = useState<Date | null>(null)
  const [orderId, setOrderId] = useState<string>('')
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false)
  const [paymentType, setPaymentType] = useState<string>('')
  const [paymentStatus, setPaymentStatus] = useState({
    purchased: false,
    status: '',
  })

  const displayDate = () => {
    const now = new Date()
    const formatDate = new Intl.DateTimeFormat(
      i18n.language === 'ko' ? 'ko-KR' : 'en-US',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour12: true,
      },
    ).format(now)

    return formatDate
  }

  const {register, handleSubmit, watch} = useForm({
    defaultValues: {
      name:
        i18n.language === 'ko'
          ? `${organization?.lastName} ${organization?.firstName}`
          : `${organization?.firstName} ${organization?.lastName}`,
      email: organization?.email ?? '',
      phoneNumber: organization?.tel.replaceAll('-', '') ?? '',
    },
  })
  const watchName = watch('name')
  const watchEmail = watch('email')
  const watchPhoneNumber = watch('phoneNumber')

  const chargeAmount = JSON.parse(
    sessionStorage.getItem('productChargeAmount') || '[]',
  )

  const getPaymentInfo = async (paymentId: string) => {
    const res = await paymentInformationApi(paymentId)
    if (res.data) {
      setOrderId(res.data.payment.paymentNo)
      setPaymentType(res.data.payment.paymentType)
    }
  }

  const makeNormalPayment = async (
    res: ShopModelResponse<ShopOrderResponse>,
  ) => {
    try {
      if (!res) return
      const data = res.data.pgPaymentRequest
      setPaymentId(res.data.payment.paymentId)
      await PortOne.requestPayment(data)
      await getPaymentInfo(res.data.payment.paymentId)
    } catch (e) {
      if (e.message) {
        onOpenFail(e.message).then(() => {
          setPaymentStatus({
            purchased: true,
            status: 'failed',
          })
        })
      } else
        onOpenFail(t('IReasonPaymentFailed')).then(() => {
          setPaymentStatus({
            purchased: true,
            status: 'failed',
          })
        })
    }
  }

  const makePaypalPayment = async (
    res: ShopModelResponse<ShopOrderResponse>,
  ) => {
    try {
      if (!res) return
      setPaymentId(res.data.payment.paymentId)
      // @ts-ignore
      await PortOne.loadPaymentUI(res.data.pgPaymentRequest, {
        onPaymentSuccess: () => {},
        onPaymentFail: () => {
          setPaymentStatus({
            purchased: true,
            status: 'failed',
          })
        },
      })
      await getPaymentInfo(res.data.payment.paymentId)
    } catch (e) {
      if (e.message) {
        onOpenFail(e.message).then(() => {
          setPaymentStatus({
            purchased: true,
            status: 'failed',
          })
        })
      } else
        onOpenFail(t('IReasonPaymentFailed')).then(() => {
          setPaymentStatus({
            purchased: true,
            status: 'failed',
          })
        })
    }
  }

  const handlePayment = async (formData: {
    name: string
    email: string
    phoneNumber: string
  }) => {
    try {
      let res
      const targetCurrency =
        organization?.country.code === NATION_CODE.KR
          ? UNIT_PRICES.KRW
          : UNIT_PRICES.USD
      if (searchParams.type === CHARGE) {
        res = await createChargeProductApi({
          licenseId: searchParams.licenseId,
          currency: targetCurrency,
          chargeItems:
            data?.offer.offerItems
              .map((item) => ({
                offerItemId: item.offerItemId,
                quantity:
                  chargeAmount.find(
                    (charge: {
                      quantity: number
                      pkgItemId: string
                      offerAmount: number
                    }) => charge.pkgItemId === item.pkgItemId,
                  )?.quantity || 0,
              }))
              .filter((item) => item.quantity > 0) || [],
          customer: {
            name: formData.name,
            email: formData?.email ?? '',
            phoneNumber: formData?.phoneNumber ?? '',
          },
          agreeToOrder: isAgreeToPurchase,
        })
      } else {
        res = await createOrderProductApi({
          goodsId: productId,
          currency: targetCurrency,
          customer: {
            name: formData.name,
            email: formData?.email ?? '',
            phoneNumber: formData?.phoneNumber ?? '',
          },
          agreeToOrder: isAgreeToPurchase,
        })
      }

      if (res) {
        setPaymentId(res.data.payment.paymentId)
        setIsPaymentLoading(true)
        if (organization?.country.code === NATION_CODE.KR) {
          makeNormalPayment(res)
        } else {
          makePaypalPayment(res)
        }
      }
    } catch (error) {
      onOpenFail(t('IReasonPaymentFailed')).then(() => {
        setPaymentStatus({
          purchased: true,
          status: 'failed',
        })
      })
    }
  }

  const dataTable = useMemo(
    () =>
      (data?.offer?.offerItems ?? [])
        .map((item) => ({
          ...item,
          cls: getCLS(item.pkgItemId),
          pkgName: getPackageName(item.pkgItemId),
          order: getOrder(item.pkgItemId),
        }))
        .toSorted((a, b) => a.cls.localeCompare(b.cls) || a.order - b.order),
    [data],
  )

  const rowSpanMap = (dataTable ?? []).reduce<Record<string, number>>(
    (acc, curr) => {
      acc[curr.cls] = (acc[curr.cls] || 0) + 1
      return acc
    },
    {},
  )
  const seenCls = new Set<string>()

  const dispatch = useAppDispatch()

  const {productName, description, paymentMethod} = useMemo(() => {
    let value = ''
    let desc = ''
    let type = ''
    let amountProduct = 0
    if (data) {
      const currentName = data.goods.goodsName.find(
        (item) => (i18n.language === 'ko' ? 'ko' : 'en') === item.langCode,
      )
      value = currentName?.name || ''
      desc = currentName?.description || ''
      if (data.sale.paymentMethod === PAYMENT_METHOD.PREPAYMENT) {
        if (data.offer.offerType === PREPAYMENT_ITEM.PROVIDER) {
          type = t('IUpfrontPlanMethod')
        }
        if (data.offer.offerType === PREPAYMENT_ITEM.MONTHLY_OFFER) {
          type = t('IMonthlyPlanMethod')
        }
      } else if (data.sale.paymentMethod === PAYMENT_METHOD.MANUAL)
        type = t('IManualPaid')
      const targetCurrency =
        organization?.country.code === 'KR' ? UNIT_PRICES.KRW : UNIT_PRICES.USD
      amountProduct =
        searchParams.amount ??
        data.offer.offerItems.reduce((total, item) => {
          if (item.offerAmount <= 0) return total
          const matchedPrice = item.unitPrices.find(
            (p) => p.currency === targetCurrency,
          )
          return (
            total +
            (matchedPrice ? matchedPrice.price * DEFAULT_ONE_QUANTITY : 0)
          )
        }, 0)
    }

    return {
      productName: value,
      description: desc,
      paymentMethod: type,
      amountProduct,
    }
  }, [data, i18n.language])

  const navigateBack = () => {
    navigate(RouteConstant.PRODUCTS_ORDER.path)
  }

  const getExpiredDate = async () => {
    const res = await getLicenseExpiredDate(productId)
    if (
      searchParams.type === PURCHASE &&
      data?.offer.usagePeriodUnit !== USAGE_PERIOD_UNIT.LIFETIME
    ) {
      setPackageEndDate(new Date(res.data.estimatedExpiredDate))
    } else if (
      searchParams.type === CHARGE &&
      data?.offer.usagePeriodUnit !== USAGE_PERIOD_UNIT.LIFETIME
    ) {
      const packageNewEndDate = new Date()
      packageNewEndDate.setMonth(
        packageNewEndDate.getMonth() + (data?.offer.usagePeriod ?? 0),
      )
      setPackageEndDate(packageNewEndDate)
    }
  }

  useEffect(() => {
    if (productId) {
      const getInitialData = async () => {
        const dataAPI = await fetchProductDetailUserApi({goodsId: productId})
        if (dataAPI.data) {
          setData(dataAPI.data)
        }
      }
      getInitialData()
      getPackageList({
        target: TYPE_INDICATOR_PRODUCT.ALL,
      })
    }
  }, [productId])

  useEffect(() => {
    if (organization) {
      const targetCurrency =
        organization?.country.code === NATION_CODE.KR
          ? UNIT_PRICES.KRW
          : UNIT_PRICES.USD

      if (targetCurrency === UNIT_PRICES.USD && data) {
        handlePayment({
          name:
            i18n.language === 'ko'
              ? `${organization?.lastName} ${organization?.firstName}`
              : `${organization?.firstName} ${organization?.lastName}`,
          email: organization?.email ?? '',
          phoneNumber: organization?.tel.replaceAll('-', '') ?? '',
        })
      }
    }
  }, [organization, data])

  useEffect(() => {
    if (data) {
      getExpiredDate()
    }
  }, [data])

  const getItemQuantity = useMemo(() => {
    return (item: OfferItem) => {
      if (searchParams.type === CHARGE) {
        const currentItem = chargeAmount.find(
          (charge: {
            offerAmount: number
            pkgItemId: string
            quantity: number
          }) => charge.pkgItemId === item.pkgItemId,
        )
        return currentItem.quantity !== 0
          ? `${currentItem.quantity} ${
              currentItem.pkgItemId === PBM_INDICATOR_ID
                ? t('IMinute')
                : t('ICase')
            }`
          : '-'
      }

      if (item.offerAmount > 10000000) {
        return t('IUnlimited')
      }

      if (item.offerAmount !== 0) {
        return `${item.offerAmount} ${
          item.offerUnit === 'Count' ? t('ICase') : t('IMinute')
        }`
      }

      return '-'
    }
  }, [searchParams])

  const getOrderTotalPrice = useMemo(() => {
    const targetCurrency =
      organization?.country.code === NATION_CODE.KR
        ? UNIT_PRICES.KRW
        : UNIT_PRICES.USD
    if (searchParams.type === CHARGE) {
      let price = 0
      data?.offer.offerItems.forEach((item) => {
        const currentItem = chargeAmount.find(
          (charge: {
            offerAmount: number
            pkgItemId: string
            quantity: number
          }) => charge.pkgItemId === item.pkgItemId,
        )
        if (currentItem) {
          const matchedPrice = item.unitPrices.find(
            (p) => p.currency === targetCurrency,
          )
          price +=
            (matchedPrice ? matchedPrice.price * currentItem.quantity : 0) || 0
        }
      })
      return `${targetCurrency} ${formatPriceNumber(
        targetCurrency === UNIT_PRICES.USD ? usdToCent(price) : price,
      )}`
    }
    if (searchParams.type === PURCHASE) {
      const salePrice =
        data?.sale.salePrice.find(
          (price: {currency: string; price: number}) =>
            price.currency === targetCurrency,
        )?.price ?? 0

      const amountProduct =
        targetCurrency === UNIT_PRICES.USD ? usdToCent(salePrice) : salePrice

      return `${targetCurrency} ${formatPriceNumber(amountProduct)}`
    }
    return 0
  }, [data, i18n.language])

  const getOrderOriginalPrice = useMemo(() => {
    const targetCurrency =
      organization?.country.code === NATION_CODE.KR
        ? UNIT_PRICES.KRW
        : UNIT_PRICES.USD
    if (searchParams.type === PURCHASE) {
      const originalPrice =
        data?.sale.price.find(
          (price: {currency: string; price: number}) =>
            price.currency === targetCurrency,
        )?.price ?? 0

      const amountProduct =
        targetCurrency === UNIT_PRICES.USD
          ? usdToCent(originalPrice)
          : originalPrice

      return `${targetCurrency} ${formatPriceNumber(amountProduct)}`
    }
    return 0
  }, [data, i18n.language])

  const isDisablePayBtn = useMemo(() => {
    return (
      !watchName.length ||
      !watchEmail.length ||
      !watchPhoneNumber.length ||
      isPaymentLoading
    )
  }, [watchName, watchEmail, watchPhoneNumber, isPaymentLoading])

  const onPaymentStatusUpdate = (item: {
    code: string
    data: string
    errorCode: null
    msg: string
  }) => {
    if (PAYMENT_FAILED.includes(item.data)) {
      setPaymentStatus({
        purchased: true,
        status: 'failed',
      })
    } else {
      setPaymentStatus({
        purchased: true,
        status: 'success',
      })
      onSetIsAgreeToPurchase(false)
    }
  }

  const navigateAfterPurchase = () => {
    if (paymentStatus.status === 'success') {
      navigate('/products-purchase-history')
    } else {
      navigate(RouteConstant.PRODUCTS_ORDER.path)
    }
  }

  const displayPackageEndDate = useMemo(() => {
    if (packageEndDate) {
      if (i18n.language !== 'en') return dateToDashString(packageEndDate)
      return format(packageEndDate, 'MMMM dd, yyyy')
    }
    if (data?.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME)
      return t('IUnlimited')
    return ''
  }, [packageEndDate, i18n.language, data])

  const showReportImage = (packageId: string) => {
    if (PACKAGE_AVAILABLE_IMAGE_ID.includes(packageId)) {
      dispatch(setReportDialogId(packageId))
      dispatch(openReportImageDialog())
    }
  }

  return (
    <div className={classes.containerProductOrderPayment}>
      <div className={classes.boxProductOrderPayment}>
        <div className={classes.productOrderPayment}>
          <div className={classes.productOrderPaymentTitle}>
            <span className={classes.notation} />
            <span>{t('IProductOrder')}</span>
          </div>
          <StompClient
            paymentId={paymentId}
            onLoadingChange={setIsPaymentLoading}
            onPaymentStatusUpdate={onPaymentStatusUpdate}
          />
          {paymentStatus.purchased && (
            <h3
              style={{
                fontSize: 24,
                marginBottom: 20,
                marginLeft: 20,
              }}
            >
              {t(
                paymentStatus.status === 'success'
                  ? 'IPaymentCompleted'
                  : 'IPaymentFailed',
              )}
            </h3>
          )}
          <div className={classes.paymentContainer}>
            <div className={classes.productInformation}>
              <div className={classes.cardTitle}>
                <div className={classes.productIcon} />
                <div>
                  {t(
                    paymentStatus.purchased
                      ? 'IPurchaseHistory'
                      : 'IProductInformation',
                  )}
                </div>
              </div>

              <div className={classes.productSection}>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {productName}
                    {data?.sale.canCharge &&
                      searchParams.type !== PURCHASE &&
                      `(${t('IChargeV1')})`}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>{paymentMethod}</span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div className={classes.infoPrice}>
                      {getOrderTotalPrice}
                    </div>
                    {getOrderTotalPrice !== getOrderOriginalPrice &&
                      searchParams.type === PURCHASE && (
                        <div
                          style={{
                            textDecoration: 'line-through',
                            opacity: 0.5,
                            fontSize: 18,
                            color: '#0cc7c7',
                          }}
                        >
                          {getOrderOriginalPrice}
                        </div>
                      )}
                  </div>
                </div>
                {paymentStatus.purchased && (
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      <strong>{displayDate()}</strong>
                    </span>
                  </div>
                )}
                {paymentStatus.purchased && (
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      <strong>{paymentType}</strong>
                    </span>
                  </div>
                )}
                {paymentStatus.purchased && (
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IOrderNumber')}: <strong>{orderId}</strong>
                    </span>
                  </div>
                )}

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {data?.offer.usagePeriodUnit !== USAGE_PERIOD_UNIT.LIFETIME
                      ? t('IUsagePeriodWithEndDate', {
                          month: data?.offer.usagePeriod
                            ? data.offer.usagePeriod
                            : 0,
                          endDate: displayPackageEndDate,
                        })
                      : t('IUnlimited')}
                  </span>
                </div>

                <div className={classes.cardTitle}>
                  <div className={classes.productIcon} />
                  {t('IDetailedItem')}
                </div>
                <TableContainer className={classes.tableContainer}>
                  <Table className={classes.table} size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align='center'
                          style={{
                            whiteSpace: 'pre',
                            fontWeight: '700',
                            padding: '8px 28px',
                          }}
                        >
                          {t('IDivision')}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            whiteSpace: 'pre',
                            fontWeight: '700',
                            padding: '8px 28px',
                          }}
                        >
                          {t('IAnalysisIndicatorsV1')}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            padding: '8px 28px',
                            whiteSpace: 'pre',
                            fontWeight: '700',
                          }}
                        >
                          {t('IPaidPoints')}
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {dataTable?.map((item, index) => {
                        const isFirstOccurCls = !seenCls.has(item.cls)
                        if (isFirstOccurCls) seenCls.add(item.cls)
                        return (
                          <TableRow key={index}>
                            {isFirstOccurCls && (
                              <TableCell
                                align='center'
                                padding='checkbox'
                                rowSpan={rowSpanMap[item.cls]}
                              >
                                {item.cls}
                              </TableCell>
                            )}
                            <TableCell
                              align='center'
                              style={{whiteSpace: 'pre', padding: '8px'}}
                            >
                              <div
                                className={
                                  PACKAGE_AVAILABLE_IMAGE_ID.includes(
                                    item.pkgItemId,
                                  )
                                    ? classes.clickableText
                                    : ''
                                }
                                onClick={() => showReportImage(item.pkgItemId)}
                              >
                                {getPackageName(item.pkgItemId)}
                              </div>
                            </TableCell>
                            <TableCell
                              align='center'
                              key={item.pkgItemId}
                              style={{whiteSpace: 'pre', padding: '8px'}}
                            >
                              {getItemQuantity(item)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <div className={classes.line} />
                {/* className={classes.periodSectionV2} */}
                <div>
                  <div className={classes.infoItem}>
                    <span
                      className={classes.infoText}
                      style={{fontSize: '20px'}}
                    >
                      {t('IDescription')} : {description ?? ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={classes.buyerInformation}>
              <div className={classes.cardTitle}>
                <div className={classes.productIcon} />
                {t('IBuyerInformation')}
              </div>

              <div className={classes.contactInfo}>
                <div className={classes.contactRow}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      width: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <div style={{display: 'flex', alignItems: 'center'}}>
                        <div className={classes.contactIcon} />
                        <span className={classes.contactText}>
                          {t('IBuyerName')}
                        </span>
                      </div>
                    </div>
                    {paymentStatus.purchased ? (
                      <strong>{watchName}</strong>
                    ) : (
                      <input
                        {...register('name')}
                        className={classes.informationInput}
                      />
                    )}
                  </div>
                </div>

                <div className={classes.contactRow}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      width: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <div className={classes.contactIcon} />
                      <span className={classes.contactText}>{t('IPhone')}</span>
                    </div>
                    {paymentStatus.purchased ? (
                      <strong>{watchPhoneNumber}</strong>
                    ) : (
                      <input
                        type='number'
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === '+' || e.key === 'e') {
                            e.preventDefault()
                          }
                        }}
                        {...register('phoneNumber')}
                        className={classes.informationInput}
                      />
                    )}
                  </div>
                </div>

                <div className={classes.contactRow}>
                  <div
                    style={{
                      alignItems: 'flex-start',
                      gap: 10,
                      width: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <div className={classes.contactIcon} />
                      <span className={classes.contactText}>{t('IEmail')}</span>
                    </div>
                    {paymentStatus.purchased ? (
                      <strong>{watchEmail}</strong>
                    ) : (
                      <input
                        {...register('email')}
                        className={classes.informationInput}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className={classes.btnWrap}>
                <div className={classes.btnChildWrap}>
                  {paymentStatus.status !== 'failed' && (
                    <div className='portone-ui-container' />
                  )}
                  {paymentStatus.status === 'success' ? (
                    <div>
                      <Button
                        color='default'
                        variant='contained'
                        onClick={navigateAfterPurchase}
                        className={classes.buttonPayment}
                      >
                        {t(
                          paymentStatus.status === 'success'
                            ? 'ICheckPurchaseHistory'
                            : 'IBuyAProduct',
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      {organization?.country.code === NATION_CODE.KR && (
                        <Button
                          color='primary'
                          disabled={isDisablePayBtn}
                          variant='contained'
                          onClick={handleSubmit(handlePayment)}
                          className={classes.buttonPayment}
                        >
                          {t('IPay')}
                        </Button>
                      )}
                      <Button
                        color='default'
                        variant='contained'
                        onClick={navigateBack}
                        className={classes.buttonPayment}
                      >
                        {t('ICancel')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductOrderPayment
